import { useEffect, useRef, useState } from "react";
import { Microphone, Stop, Sparkle } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";

const CHUNK_MS = 4 * 60 * 1000; // each dictation chunk is capped at 4 minutes — nothing ever times out

export default function CarePlanDictation({ clientId, onApply }) {
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [uploading, setUploading] = useState(0);
  const [drafting, setDrafting] = useState(false);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const chunkTimerRef = useRef(null);

  useEffect(() => () => { stopAll(); }, []); // eslint-disable-line

  const stopAll = () => {
    clearInterval(timerRef.current);
    clearInterval(chunkTimerRef.current);
    try { recRef.current?.state !== "inactive" && recRef.current?.stop(); } catch { /* noop */ }
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
  };

  const transcribeBlob = async (blob) => {
    if (!blob || blob.size < 500) return;
    setUploading((n) => n + 1);
    try {
      const fd = new FormData();
      fd.append("file", blob, "dictation.webm");
      const { data } = await api.post("/transcribe", fd, { timeout: 120000 });
      if (data.text) setTranscript((t) => (t ? t + " " : "") + data.text);
    } catch (e) {
      toast.error("A chunk failed to transcribe — your earlier audio is safe; keep going or retry");
    } finally {
      setUploading((n) => n - 1);
    }
  };

  const startSegment = () => {
    const rec = recRef.current;
    if (!rec || rec.state === "inactive") return;
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
    rec.onstop = () => transcribeBlob(new Blob(chunks, { type: rec.mimeType || "audio/webm" }));
    rec.start();
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      recRef.current = new MediaRecorder(stream);
      setRecording(true);
      setElapsed(0);
      startSegment();
      const started = Date.now();
      timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
      // every 4 minutes: seal the chunk (uploads + transcribes in background) and start a fresh one
      chunkTimerRef.current = setInterval(() => {
        try { recRef.current?.stop(); } catch { /* noop */ }
        startSegment();
      }, CHUNK_MS);
    } catch {
      toast.error("Microphone access denied — allow the mic to dictate");
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    clearInterval(chunkTimerRef.current);
    setRecording(false);
    try { recRef.current?.stop(); } catch { /* noop */ }
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
  };

  const draft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/careplan/dictate-draft`, { transcript }, { timeout: 180000 });
      onApply(data.draft || {});
      toast.success("Drafted — review every field, then Save");
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDrafting(false); }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="border-primary/40 text-primary hover:bg-accent" data-testid="dictate-careplan-button">
        <Microphone size={16} className="mr-1.5" /> Dictate care plan
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 p-4" role="dialog" aria-modal="true" aria-label="Dictate the care plan" data-testid="dictation-dialog">
          <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl">
            <p className="font-heading text-lg font-semibold text-stone-900">Dictate the care plan</p>
            <p className="mt-1 text-xs text-stone-500">
              Talk like you're telling a new caregiver about the client. Recording saves in 4-minute chunks as you
              go — nothing is lost if you pause, get interrupted, or close the browser. There is no time limit.
            </p>
            <div className="mt-4 flex items-center justify-center gap-4">
              {!recording ? (
                <Button onClick={start} className="rounded-full bg-primary px-6 text-primary-foreground" data-testid="dictation-start">
                  <Microphone size={18} className="mr-2" /> Start dictating
                </Button>
              ) : (
                <Button onClick={stop} variant="outline" className="rounded-full border-[#C0392B] px-6 text-[#C0392B]" data-testid="dictation-stop">
                  <Stop size={18} className="mr-2" /> Stop ({mm}:{ss})
                </Button>
              )}
            </div>
            {recording && <p className="mt-2 text-center text-[11px] text-stone-400">Recording… a red dot means we're listening. Take your time.</p>}
            {uploading > 0 && <p className="mt-1 text-center text-[11px] text-stone-400">Transcribing {uploading} chunk(s)…</p>}
            {transcript && (
              <div className="mt-4 max-h-48 overflow-y-auto rounded-lg border border-stone-200 bg-accent/40 p-3" data-testid="dictation-transcript">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">What we heard</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-stone-700">{transcript}</p>
              </div>
            )}
            <div className="mt-5 flex justify-between">
              <Button variant="ghost" onClick={() => { stop(); setOpen(false); }} className="text-xs text-stone-500" data-testid="dictation-cancel">Close</Button>
              <Button onClick={draft} disabled={drafting || uploading > 0 || transcript.trim().length < 40}
                className="rounded-full bg-primary text-primary-foreground" data-testid="dictation-draft">
                <Sparkle size={16} className="mr-1.5" /> {drafting ? "Drafting…" : "Draft care plan from this"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
